"use client";

// ===========================================
// Paylaşımlı Pricing Kartları
// ===========================================
//
// Hem /for-hosts pazarlama sayfası hem profil "Abonelik" tab'ı bu bileşeni
// kullanır. Plan verisi tek kaynaktan (lib/constants → SUBSCRIPTION_PLANS) okunur.
//
// Modlar:
//   - "marketing" (varsayılan): Tüm planlarda CTA gösterir, mevcut plan vurgulanmaz.
//   - "subscription": Aktif plan "Mevcut Planınız" rozeti ile vurgulanır;
//     CTA'lar "Yükselt"/"Düşür"/"Mevcut Planınız" şeklinde değişir.

import { useState } from "react";
import { CheckCircle2, TrendingUp, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  PLAN_RANK,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PLAN_KEYS,
  YEARLY_DISCOUNT,
  type BillingPeriod,
  type SubscriptionPlanKey,
} from "@/lib/constants";

export type PricingMode = "marketing" | "subscription";

interface PricingCardsProps {
  mode?: PricingMode;
  /** subscription modunda mevcut planın anahtarı */
  activePlanKey?: SubscriptionPlanKey | null;
  /** marketing modunda CTA tıklamasında çağrılır (genelde auth modal açar veya /listings/new'a yönlendirir) */
  onMarketingCta?: (planKey: SubscriptionPlanKey) => void;
  /** subscription modunda yükseltme/düşürme tıklandığında çağrılır */
  onSelectPlan?: (planKey: SubscriptionPlanKey, period: BillingPeriod) => void;
  /** opsiyonel: dış container className override'ı */
  className?: string;
}

const formatTRY = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
  }).format(amount);

// PLAN_RANK artık lib/constants.ts'te — sunucu ve istemci AYNI tabloyu kullanıyor (K-9).

export function PricingCards({
  mode = "marketing",
  activePlanKey = null,
  onMarketingCta,
  onSelectPlan,
  className,
}: PricingCardsProps) {
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  const isYearly = period === "yearly";

  return (
    <div className={cn("w-full", className)}>
      <div className="text-center">
        {/* Aylık / Yıllık toggle */}
        <div className="inline-flex items-center gap-1 rounded-full bg-white p-1.5 shadow-sm ring-1 ring-slate-200">
          <button
            type="button"
            onClick={() => setPeriod("monthly")}
            className={cn(
              "rounded-full px-6 py-2 text-sm font-medium transition-all",
              !isYearly
                ? "bg-primary text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Aylık
          </button>
          <button
            type="button"
            onClick={() => setPeriod("yearly")}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-6 py-2 text-sm font-medium transition-all",
              isYearly
                ? "bg-primary text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            Yıllık
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-semibold",
                isYearly ? "text-primary bg-white" : "bg-green-100 text-green-700"
              )}
            >
              %{Math.round(YEARLY_DISCOUNT * 100)}
            </span>
          </button>
        </div>
      </div>

      {/* Mobilde YATAY KAYDIRMALI, md+ üç sütun grid.
          ⚠️ DİKEY DOLGU ZORUNLU (`pt-7 pb-6`): kartların üstünde `-top-4` ile
          TAŞAN rozetler var ("En Çok Tercih Edilen", "Mevcut Planınız") ve
          popüler kartta `scale-[1.02]` + `shadow-xl`. CSS'te `overflow-x: auto`
          dikey ekseni de `auto`ya çevirir; dolgu olmasaydı rozetler KESİLİR ya
          da istenmeyen dikey kaydırma çubuğu çıkardı.
          ⚠️ NEGATİF KENAR BOŞLUĞU (-mx-*) KULLANILMADI: bu bileşen iki farklı
          sayfada, farklı dolgulu kapsayıcıların içinde duruyor; taşma sayfayı
          yatay kaydırtabilirdi.
          `scroll-snap` ile kartlar hizada durur; `basis-[86%]` bir sonraki
          karttan bir parça göstererek kaydırılabilir olduğunu belli eder. */}
      <div className="scrollbar-hide mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pt-7 pb-6 md:grid md:grid-cols-3 md:gap-6 md:overflow-visible md:pt-0 md:pb-0">
        {SUBSCRIPTION_PLAN_KEYS.map((key) => {
          const plan = SUBSCRIPTION_PLANS[key];
          const current = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
          const original = isYearly ? plan.yearlyPriceOriginal : plan.monthlyPriceOriginal;
          const isFree = plan.monthlyPrice === 0;
          const isActive = mode === "subscription" && activePlanKey === key;
          const isPopular = plan.popular;

          // CTA mantığı
          let ctaLabel: string = plan.ctaLabel;
          let ctaDisabled = false;
          let ctaVariant: "primary" | "outline" | "ghost" = isPopular ? "primary" : "outline";

          if (mode === "subscription" && activePlanKey) {
            const activeRank = PLAN_RANK[activePlanKey];
            const targetRank = PLAN_RANK[key];
            if (isActive) {
              ctaLabel = "Mevcut Planınız";
              ctaDisabled = true;
              ctaVariant = "ghost";
            } else if (targetRank > activeRank) {
              ctaLabel = `${plan.name}'a Yükselt`;
              ctaVariant = "primary";
            } else if (key === "free") {
              // Plus/Pro kullanıcısı Free kartını görüyor — downgrade butonu KAPALI.
              // İptal için üstteki "Mevcut Planınız" kartındaki "Aboneliği İptal Et" butonunu kullanmalı.
              ctaLabel = "Mevcut planınızdan iptal edin";
              ctaDisabled = true;
              ctaVariant = "ghost";
            } else {
              ctaLabel = `${plan.name}'a Geç`;
              ctaVariant = "outline";
            }
          }

          return (
            <div
              key={key}
              className={cn(
                "relative flex flex-col rounded-3xl border-2 bg-white p-6 transition-all duration-300",
                // Mobil carousel: kartlar sabit oranda, hizalanarak kayar.
                // md+ grid devraldığı için bu sınırlar kalkar.
                "w-[86%] shrink-0 snap-start md:w-auto md:shrink",
                isPopular
                  ? "border-primary shadow-primary/10 scale-[1.02] shadow-xl"
                  : "border-border/60 hover:border-primary/40 hover:shadow-lg",
                isActive && "ring-primary/30 ring-4"
              )}
            >
              {/* Üst rozetler */}
              {isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap text-white shadow-lg">
                    <TrendingUp className="h-4 w-4" />
                    En Çok Tercih Edilen
                  </span>
                </div>
              )}
              {isActive && (
                <div className="absolute -top-4 right-4">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow-lg">
                    <Sparkles className="h-3.5 w-3.5" />
                    Mevcut Planınız
                  </span>
                </div>
              )}

              {/* Başlık */}
              <div className="mb-6">
                <h3 className="text-xl font-bold">{plan.name}</h3>
                <p className="text-muted-foreground mt-1 text-sm">{plan.tagline}</p>
              </div>

              {/* Fiyat */}
              <div className="mb-6">
                {isFree ? (
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">Ücretsiz</span>
                  </div>
                ) : (
                  <>
                    {original !== null && original > current && (
                      <div className="text-muted-foreground/80 text-sm line-through">
                        {formatTRY(original)}
                      </div>
                    )}
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold">{formatTRY(current)}</span>
                      <span className="text-muted-foreground ml-1.5 text-sm">
                        {isYearly ? "/yıl" : "/ay"}
                      </span>
                    </div>
                    {isYearly && (
                      <p className="mt-2 text-sm font-medium text-green-600">
                        Yıllık ödemede %{Math.round(YEARLY_DISCOUNT * 100)} indirim
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Özellikler */}
              <ul className="mb-8 flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                className={cn(
                  "w-full rounded-full",
                  ctaVariant === "primary" && "bg-primary hover:bg-primary/90",
                  ctaVariant === "outline" && "bg-foreground hover:bg-foreground/90 text-white",
                  ctaVariant === "ghost" && "bg-slate-100 text-slate-500 hover:bg-slate-100"
                )}
                disabled={ctaDisabled}
                onClick={() => {
                  if (ctaDisabled) return;
                  if (mode === "subscription") {
                    onSelectPlan?.(key, period);
                  } else {
                    onMarketingCta?.(key);
                  }
                }}
              >
                {ctaLabel}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
